import { useState, useEffect } from "react"
import { X, Megaphone, Radio } from "lucide-react"
import { CONVERSATION_NAME_MAX, CONVERSATION_NAME_MIN } from "./name-limits"

interface EditBroadcastPageProps {
  broadcastId: string
  currentName: string
  onClose: () => void
  onSave: (data: { name: string }) => void
}

export function EditBroadcastPage({
  broadcastId: _broadcastId,
  currentName,
  onClose,
  onSave,
}: EditBroadcastPageProps) {
  const [name, setName] = useState(currentName)
  const [hasChanges, setHasChanges] = useState(false)
  const [nameError, setNameError] = useState("")

  useEffect(() => {
    setHasChanges(name.trim() !== currentName)
  }, [name, currentName])

  // Over the limit before a key is pressed: `maxLength` never applied to the
  // value we seeded from the stored name, so an existing over-long name is
  // shown as-is rather than silently truncated — losing someone's name on open
  // would be worse than asking them to shorten it.
  const overLimit = name.length > CONVERSATION_NAME_MAX

  const handleSave = () => {
    const trimmed = name.trim()
    if (trimmed.length < CONVERSATION_NAME_MIN) {
      setNameError(`Broadcast name must be at least ${CONVERSATION_NAME_MIN} characters`)
      return
    }
    // The missing half of the check. Without it an over-long name saved
    // cleanly, which is how a name longer than the stated maximum existed in
    // the first place.
    if (trimmed.length > CONVERSATION_NAME_MAX) {
      setNameError(`Broadcast name must be ${CONVERSATION_NAME_MAX} characters or fewer`)
      return
    }
    onSave({ name: trimmed })
  }

  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-4 border-b border-border shrink-0">
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="flex-1 text-lg font-semibold text-foreground">Edit Broadcast</h2>
        <button
          onClick={handleSave}
          disabled={!hasChanges || overLimit}
          className="px-4 py-1.5 rounded-lg font-medium text-sm transition-colors disabled:text-muted-foreground text-primary hover:bg-primary/10 disabled:hover:bg-transparent"
        >
          Save
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Broadcast Icon */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-28 h-28 rounded-full bg-muted flex items-center justify-center">
            <Megaphone className="w-12 h-12 text-muted-foreground" />
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Broadcast lists are a simple way to send messages to multiple people at once
          </p>
        </div>

        {/* Broadcast Name */}
        <div className="mb-6">
          <label className="block text-xs font-medium text-muted-foreground mb-2">Broadcast name</label>
          <input
            value={name}
            onChange={e => { setName(e.target.value); if (nameError) setNameError("") }}
            maxLength={CONVERSATION_NAME_MAX}
            placeholder="Enter broadcast name"
            className={`w-full rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none border ${nameError ? 'border-destructive bg-destructive/5' : 'border-none bg-muted'}`}
          />
          <div className="flex justify-between mt-2">
            {nameError ? <p className="text-xs text-destructive">{nameError}</p> : <span />}
            <p className={`text-xs ${overLimit ? "text-destructive" : "text-muted-foreground"}`}>
              {name.length}/{CONVERSATION_NAME_MAX}
            </p>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-muted/50 rounded-xl">
          <div className="flex items-start gap-3">
            <Radio className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Messages sent to a broadcast list are received as individual messages by each recipient. Recipients won't be able to see other recipients in the list.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
